/**
 * Rotating announcement bar.
 *
 * Rotation pauses on hover and on focus, and stops entirely for visitors who
 * prefer reduced motion — an auto-advancing banner is a motion trap for them.
 */
(function () {
  'use strict';

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      if (this.slides.length < 2) return;

      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.autorotate =
        this.dataset.autorotate === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => {
        this.go(this.index - 1);
        this.restart();
      });

      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => {
        this.go(this.index + 1);
        this.restart();
      });

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());

      // Don't burn cycles rotating a bar nobody is looking at. Kept as a
      // field so disconnectedCallback can detach it — the Theme Editor
      // reconnects this element on every setting change, and an orphaned
      // listener would keep calling start() on a detached bar.
      this.onVisibility = () => {
        if (document.hidden) this.stop();
        else this.start();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    go(next) {
      const total = this.slides.length;
      this.slides[this.index].hidden = true;
      this.index = ((next % total) + total) % total;
      this.slides[this.index].hidden = false;
    }

    start() {
      if (!this.autorotate || this.timer) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
})();
